import {
  listQueueItems,
  markQueueError,
  markQueueSending,
  markQueueSuccess
} from "./db.js";
import { supabase, supabaseConfig, supabasePing } from "./supabase.js";
import { buildAdminDashboard } from "./dashboard.js";

export function isRetryableSyncError(error) {
  const code = Number(error?.status || error?.code || 0);
  if (!code) return true;
  return code === 408 || code === 425 || code === 429 || (code >= 500 && code <= 599);
}

function wait(milliseconds) {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

async function withRetry(operation, onRetry = () => {}, maximumAttempts = 3) {
  let lastError;
  for (let attempt = 1; attempt <= maximumAttempts; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      const canRetry = (
        attempt < maximumAttempts
        && globalThis.navigator?.onLine !== false
        && isRetryableSyncError(error)
      );
      if (!canRetry) throw error;
      const delay = (400 * (2 ** (attempt - 1))) + Math.round(Math.random() * 150);
      onRetry({ attempt: attempt + 1, maximumAttempts, delay, error });
      await wait(delay);
    }
  }
  throw lastError;
}

export function interviewDocument(interview, requestId, user, profile) {
  const answersProfile = interview.responses.profile;
  const mobility = interview.responses.mobility;
  const fieldContext = interview.responses.field_context || {};
  const document = {
    interview_id: interview.id,
    respondent_id: interview.respondentId || interview.id,
    revision: Number(interview.revision),
    phase: interview.phase,
    experiment_id: interview.experimentId,
    experiment_version: Number(interview.experimentVersion),
    questionnaire_version: Number(interview.questionnaireVersion),
    app_version: interview.appVersion,
    interviewer_code: interview.interviewerCode,
    device_id: interview.deviceId,
    block_id: interview.designSnapshot.id,
    started_at: interview.startedAt,
    finished_at: interview.finishedAt,
    duration_seconds: Number(interview.durationSeconds || 0),
    gender_identity: answersProfile.gender_identity || null,
    age_years: answersProfile.age_years == null ? null : Number(answersProfile.age_years),
    origin_city: mobility.origin_city || null,
    origin_neighborhood: mobility.origin_neighborhood || null,
    metro_habit: mobility.metro_habit || null,
    is_test: interview.is_test === true,
    duplicate_attempt: interview.duplicate_attempt === true,
    treatment_group: fieldContext.treatment_group,
    trat_i: fieldContext.TRAT_i == null ? null : Number(fieldContext.TRAT_i),
    approach_type: fieldContext.approach_type,
    line_or_location: fieldContext.line_or_location,
    reference_station: fieldContext.reference_station || null,
    pitaguari_serves_stop: fieldContext.pitaguari_serves_stop == null
      ? null
      : Boolean(fieldContext.pitaguari_serves_stop),
    deletion_requested: Boolean(interview.deletion?.requested),
    payload_json: interview,
    request_id: requestId,
    client_updated_at: interview.updatedAt || new Date().toISOString(),
    synced_by: user.id,
    researcher_email: profile.email || user.email,
    researcher_name: profile.name || user.user_metadata?.name || user.email
  };
  if (mobility.neighborhood_catalog_version != null) {
    document.neighborhood_catalog_version = Number(mobility.neighborhood_catalog_version);
  }
  if (interview.deletion?.reason) document.deletion_reason = interview.deletion.reason;
  if (interview.deletion?.requestedAt) document.deletion_requested_at = interview.deletion.requestedAt;
  return document;
}

async function currentUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) {
    throw new Error("A sessão do Supabase expirou. Entre novamente antes de sincronizar.");
  }
  const { data: profile, error: profileError } = await supabase
    .from(supabaseConfig.profilesTable)
    .select("id,email,name,role,status,must_change_password")
    .eq("id", data.user.id)
    .single();
  if (profileError || !profile?.status) {
    throw new Error("A conta não possui um perfil ativo para sincronização.");
  }
  return { user: data.user, profile };
}

async function upsertInterview(item, user, profile) {
  const documentData = interviewDocument(item.payload, item.requestId, user, profile);
  const { data: existing, error: existingError } = await supabase
    .from(supabaseConfig.interviewsTable)
    .select("interview_id,revision")
    .eq("interview_id", item.interviewId)
    .maybeSingle();
  if (existingError) throw existingError;

  if (Number(existing?.revision || 0) > Number(item.revision || 0)) {
    return {
      operation: "stale_revision_ignored",
      document_id: existing.interview_id,
      revision: existing.revision
    };
  }

  const operation = existing ? "updated" : "inserted";
  const { data, error } = await supabase
    .from(supabaseConfig.interviewsTable)
    .upsert(documentData, { onConflict: "interview_id" })
    .select("interview_id,revision,created_at,updated_at")
    .single();
  if (error) throw error;
  return {
    operation,
    document_id: data.interview_id,
    revision: data.revision,
    created_at: data.created_at,
    updated_at: data.updated_at
  };
}

async function registerDeletion(item, user, profile) {
  const deletion = item.payload;
  const update = {
    deletion_requested: true,
    deletion_reason: deletion.reason,
    deletion_requested_at: deletion.requested_at,
    request_id: item.requestId,
    client_updated_at: new Date().toISOString(),
    synced_by: user.id,
    researcher_email: profile.email || user.email,
    researcher_name: profile.name || user.user_metadata?.name || user.email
  };
  const { error: interviewError } = await supabase
    .from(supabaseConfig.interviewsTable)
    .update(update)
    .eq("interview_id", deletion.interview_id);
  if (interviewError) throw interviewError;

  const { error: requestError } = await supabase
    .from(supabaseConfig.deletionsTable)
    .upsert({
      request_id: item.requestId,
      interview_id: deletion.interview_id,
      revision: Number(deletion.revision || 0),
      reason: deletion.reason,
      requested_at: deletion.requested_at,
      interviewer_code: deletion.interviewer_code,
      device_id: deletion.device_id,
      requested_by: user.id
    }, {
      onConflict: "request_id",
      ignoreDuplicates: true
    });
  if (requestError) throw requestError;
  return { operation: "deletion_recorded", document_id: deletion.interview_id };
}

export async function synchronizePending(_settings, experiment, onProgress = () => {}) {
  if (!navigator.onLine) throw new Error("O aparelho está sem conexão com a internet.");
  const { user, profile } = await currentUser();
  const items = await listQueueItems({ statuses: ["pending", "error"] });
  const result = { total: items.length, success: 0, errors: 0, details: [] };
  onProgress({ ...result, current: 0, state: "starting" });

  for (let index = 0; index < items.length; index += 1) {
    const item = await markQueueSending(items[index].id);
    onProgress({ ...result, current: index + 1, item, state: "sending" });
    try {
      const response = await withRetry(
        () => item.action === "request_deletion"
          ? registerDeletion(item, user, profile)
          : upsertInterview(item, user, profile),
        retry => onProgress({
          ...result,
          current: index + 1,
          item,
          retry,
          state: "retrying"
        })
      );
      await markQueueSuccess(item.id, response);
      result.success += 1;
      result.details.push({ id: item.id, ok: true, response });
      onProgress({ ...result, current: index + 1, item, state: "success" });
    } catch (error) {
      await markQueueError(item.id, error);
      result.errors += 1;
      result.details.push({ id: item.id, ok: false, error: error.message });
      onProgress({ ...result, current: index + 1, item, error, state: "error" });
    }
  }
  onProgress({ ...result, current: items.length, state: "complete" });
  return result;
}

export async function testConnection() {
  const [health, identity] = await Promise.all([supabasePing, currentUser()]);
  if (!health.ok) throw new Error(health.error || "O Supabase não respondeu à verificação.");
  const { error } = await supabase
    .from(supabaseConfig.interviewsTable)
    .select("interview_id")
    .limit(1);
  if (error) throw error;
  return {
    service: "Supabase",
    project: supabaseConfig.projectName,
    user: identity.profile.email
  };
}

async function listAllRows(table, orderColumn) {
  const rows = [];
  const pageSize = 1000;
  let offset = 0;
  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select("*")
      .order(orderColumn, { ascending: true })
      .range(offset, offset + pageSize - 1);
    if (error) throw error;
    rows.push(...data);
    if (data.length < pageSize) break;
    offset += pageSize;
  }
  return rows;
}

export function fetchAllInterviewDocuments() {
  return listAllRows(supabaseConfig.interviewsTable, "interview_id");
}

export function fetchAllDeletionRequests() {
  return listAllRows(supabaseConfig.deletionsTable, "request_id");
}

export async function fetchAdminDashboard() {
  if (!navigator.onLine) throw new Error("O computador está sem conexão com a internet.");
  const { profile } = await currentUser();
  if (!["admin", "superuser"].includes(profile.role)) {
    throw new Error("A conta atual não possui acesso ao painel de acompanhamento.");
  }
  const documents = await fetchAllInterviewDocuments();
  return { dashboard: buildAdminDashboard(documents), documents };
}
